"use client";

import { useState, useEffect, useMemo, ReactNode } from "react";
import { createBrowserClient } from "@/lib/supabase/client";
import { SettingsContext } from "@/hooks/auth/useSettings";
import { Settings } from "@/types/auth";

const supabase = createBrowserClient();

export const SettingsProvider = ({ children }: { children: ReactNode }) => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(true);

  const fetchSettings = async () => {
    setSettingsLoading(true);
    try {
      const { data, error } = await supabase.from("settings").select("*").limit(1).single();
      if (error) {
        // If no settings found or RLS error, silently handle it
        if (error.code === 'PGRST116' || error.message?.includes('no rows')) {
          console.warn("No settings found in database");
        } else {
          console.error("Error fetching settings:", error.message || error);
        }
        setSettings(null);
      } else {
        setSettings(data);
      }
    } catch (err) {
      console.error("Unexpected error fetching settings:", err);
      setSettings(null);
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const updateSettings = async (updates: Partial<Settings>) => {
    if (!settings?.id) return;

    const { data, error } = await supabase
      .from("settings")
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", settings.id)
      .select()
      .single();

    if (error) {
      console.error("Error updating settings:", error);
      throw error;
    }

    // Update local state with the returned data
    setSettings(data);
  };

  const settingsContextValue = useMemo(
    () => ({
      settings,
      settingsLoading,
      updateSettings,
    }),
    [settings, settingsLoading]
  );

  return <SettingsContext.Provider value={settingsContextValue}>{children}</SettingsContext.Provider>;
};
